import { Mail, MapPin, Phone, Handshake } from "lucide-react";
import { Link } from "react-router-dom";
import { FOOTER } from "@/constants/testIds";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_ps-medical-logistics/artifacts/1dt4h2l7_Gemini_Generated_Image_dpwsejdpwsejdpws.png";

export default function Footer() {
  return (
    <footer
      data-testid={FOOTER.section}
      className="bg-[#0F172A] text-slate-300"
    >
      {/* ---- Main footer grid ---- */}
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20">
        <div className="grid lg:grid-cols-12 gap-12">
          {/* Brand + mission */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <img
                src={LOGO_URL}
                alt="PS GLOBAL SERIES, LLC"
                className="h-10 w-10 object-contain bg-white/5 p-1 rounded-sm"
              />
              <div>
                <div
                  data-testid={FOOTER.corporateName}
                  className="font-display text-white font-bold tracking-tight"
                >
                  PS GLOBAL SERIES, LLC
                </div>
                <div className="eyebrow text-slate-400 mt-1">
                  B2B Sourcing Agency · US–DR Trade
                </div>
              </div>
            </div>
            <p className="mt-6 max-w-md text-sm leading-relaxed text-slate-400">
              A sourcing agency and trade liaison connecting US buyers with
              vetted Dominican Republic suppliers — supplier matchmaking,
              cross-border negotiation, and procurement oversight across all
              industries.
            </p>

            <div className="mt-6 inline-flex items-center gap-2 border border-white/10 bg-white/5 px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] font-bold text-slate-200">
              <Handshake size={13} className="text-emerald-400" />
              US ↔ DR Cross-Border Procurement
            </div>
          </div>

          {/* Corporate footprint */}
          <div className="lg:col-span-4">
            <div className="eyebrow text-slate-400">Corporate Footprint</div>
            <div className="mt-5 space-y-4 text-sm">
              <div>
                <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-500">
                  Official Name
                </div>
                <div className="mt-1 text-white font-medium">
                  PS GLOBAL SERIES, LLC
                </div>
              </div>

              <address
                data-testid={FOOTER.addressBlock}
                className="not-italic"
              >
                <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-500">
                  US Registered Agent / Address
                </div>
                <div className="mt-1 flex items-start gap-2 text-slate-200 leading-relaxed">
                  <MapPin
                    size={15}
                    className="mt-0.5 text-[#B45309] flex-shrink-0"
                  />
                  <span>
                    7345 W Sand Lake Rd, Ste 210, Office 7485
                    <br />
                    Orlando, FL 32819
                    <br />
                    United States
                  </span>
                </div>
              </address>

              <div>
                <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-500">
                  Corporate Contact
                </div>
                <ul className="mt-1.5 space-y-1.5">
                  <li>
                    <a
                      href="mailto:info@psglobalseries.com"
                      data-testid={FOOTER.emailLink}
                      className="inline-flex items-center gap-2 text-slate-200 hover:text-white transition-colors"
                    >
                      <Mail size={14} className="text-slate-400" />
                      info@psglobalseries.com
                    </a>
                  </li>
                  <li>
                    <a
                      href="tel:+13395658894"
                      data-testid="footer-phone-link"
                      className="inline-flex items-center gap-2 text-slate-200 hover:text-white transition-colors"
                    >
                      <Phone size={14} className="text-slate-400" />
                      +1 (339) 565-8894
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="lg:col-span-3">
            <div className="eyebrow text-slate-400">Company</div>
            <ul className="mt-5 space-y-3 text-sm">
              <li>
                <a href="/#services" className="hover:text-white transition-colors">
                  Services
                </a>
              </li>
              <li>
                <a
                  href="/#methodology"
                  className="hover:text-white transition-colors"
                >
                  How It Works
                </a>
              </li>
              <li>
                <a
                  href="/#engagement"
                  className="hover:text-white transition-colors"
                >
                  Engagement &amp; Billing
                </a>
              </li>
              <li>
                <a
                  href="/#compliance"
                  className="hover:text-white transition-colors"
                >
                  Compliance
                </a>
              </li>
              <li>
                <a
                  href="mailto:info@psglobalseries.com"
                  className="hover:text-white transition-colors"
                >
                  Contact
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* ---- Disclaimer band ---- */}
      <div className="border-t border-white/10">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-8">
          <p
            data-testid={FOOTER.disclaimer}
            className="text-[12px] leading-relaxed text-slate-500"
          >
            <span className="font-semibold text-slate-400">Disclaimer:</span>{" "}
            PS Global Series, LLC operates as a B2B sourcing agency and trade
            liaison. We facilitate connections, negotiations, and procurement
            between US buyers and Dominican Republic suppliers. We act strictly
            as intermediaries and consultants, and do not manufacture, take
            ownership of, or directly distribute the physical goods traded.
          </p>
        </div>
      </div>

      {/* ---- Bottom bar: copyright + corporate address + legal links ---- */}
      <div className="border-t border-white/10">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-6 flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <p
              data-testid={FOOTER.copyright}
              className="text-xs text-slate-500 tracking-wide"
            >
              © 2026 PS GLOBAL SERIES, LLC. All rights reserved.
            </p>
            <ul className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs">
              <li>
                <Link
                  to="/contact"
                  data-testid="footer-link-contact"
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  Contact
                </Link>
              </li>
              <li>
                <Link
                  to="/terms-of-service"
                  data-testid={FOOTER.linkTerms}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link
                  to="/privacy-policy"
                  data-testid={FOOTER.linkPrivacy}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/refund-policy"
                  data-testid={FOOTER.linkRefundShipping}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  Cancellation &amp; Refund Policy
                </Link>
              </li>
            </ul>
          </div>
          <p className="text-[11px] text-slate-500 leading-relaxed">
            PS GLOBAL SERIES, LLC · Formed in the State of New Mexico, USA ·
            Mailing address: 7345 W Sand Lake Rd, Ste 210, Office 7485,
            Orlando, FL 32819, United States ·{" "}
            <a
              href="mailto:info@psglobalseries.com"
              className="text-slate-400 hover:text-white"
            >
              info@psglobalseries.com
            </a>{" "}
            · +1 (339) 565-8894
          </p>
        </div>
      </div>
    </footer>
  );
}
